import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  LogOut, Users, Download, Trash2, Star, Plus, Save, MessageSquare,
  HelpCircle, Settings as SettingsIcon, LayoutGrid, Loader2,
} from "lucide-react";
import { api, API, LOGO_URL } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { LEAD_STATUSES } from "../../data/content";

const STATUS_COLORS = {
  new: "bg-[#2563EB]/20 text-[#60A5FA] border-[#2563EB]/40",
  contacted: "bg-[#F59E0B]/20 text-[#FBBF24] border-[#F59E0B]/40",
  "follow-up": "bg-[#8B5CF6]/20 text-[#A78BFA] border-[#8B5CF6]/40",
  converted: "bg-[#10B981]/20 text-[#34D399] border-[#10B981]/40",
  closed: "bg-gray-500/20 text-gray-400 border-gray-500/40",
};
const field = "w-full bg-[#0B132B] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:border-[#D4AF37] focus:outline-none";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("leads");
  const [stats, setStats] = useState({});
  const [leads, setLeads] = useState([]);
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [settings, setSettings] = useState({});
  const [selected, setSelected] = useState(null);

  useEffect(() => { if (user === false) nav("/admin"); }, [user, nav]);

  const load = useCallback(async () => {
    try {
      const [st, ld, ct] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/leads"), api.get("/content"),
      ]);
      setStats(st.data); setLeads(ld.data);
      setServices(ct.data.services || []); setTestimonials(ct.data.testimonials || []);
      setFaqs(ct.data.faqs || []); setSettings(ct.data.settings?.settings || {});
    } catch (e) { if (e.response?.status === 401) nav("/admin"); }
  }, [nav]);

  useEffect(() => { if (user && user.email) load(); }, [user, load]);

  if (!user || user === false) {
    return <div className="min-h-screen flex items-center justify-center bg-[#060A17]"><Loader2 className="animate-spin text-[#D4AF37]" /></div>;
  }

  const updateLead = async (id, patch) => {
    const { data } = await api.patch(`/admin/leads/${id}`, patch);
    setLeads((ls) => ls.map((l) => (l.id === id ? data : l)));
    if (selected?.id === id) setSelected(data);
    load();
  };
  const delLead = async (id) => { await api.delete(`/admin/leads/${id}`); setSelected(null); load(); toast.success("Lead deleted"); };
  const exportCsv = () => window.open(`${API}/admin/leads/export`, "_blank");

  const statCards = [
    { k: "total", l: "TOTAL LEADS", icon: Users }, { k: "dispatch", l: "DISPATCH" },
    { k: "training", l: "TRAINING" }, { k: "startup", l: "STARTUP" },
    { k: "audit", l: "AUDIT" }, { k: "hr", l: "HR / BACK-OFFICE" },
  ];

  const TABS = [
    { k: "leads", l: "Leads", icon: Users }, { k: "services", l: "Pricing", icon: LayoutGrid },
    { k: "testimonials", l: "Testimonials", icon: Star }, { k: "faqs", l: "FAQs", icon: HelpCircle },
    { k: "settings", l: "Settings", icon: SettingsIcon },
  ];

  return (
    <div className="min-h-screen bg-[#060A17]" data-testid="admin-dashboard">
      <header className="glass border-b border-[#D4AF37]/15 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3"><img src={LOGO_URL} alt="" className="h-9 w-auto" /><span className="hidden sm:block text-xs text-gray-400">Admin Dashboard</span></div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-gray-300">{user.name}</span>
            <button onClick={() => { logout(); nav("/admin"); }} className="flex items-center gap-2 text-sm text-gray-300 hover:text-[#D4AF37]" data-testid="admin-logout"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {statCards.map((c, i) => (
            <motion.div key={c.k} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="nm-card rounded-xl p-5" data-testid={`stat-${c.k}`}>
              <p className="font-mono text-[10px] tracking-wider text-gray-400">{c.l}</p>
              <p className="font-head text-3xl font-black nm-gold-text mt-1">{stats[c.k] ?? 0}</p>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${tab === t.k ? "nm-gold-bg text-[#0B132B]" : "nm-card text-gray-300 hover:text-white"}`}
              data-testid={`tab-${t.k}`}>
              <t.icon size={15} /> {t.l}
            </button>
          ))}
        </div>

        {tab === "leads" && (
          <div>
            <div className="flex justify-end mb-4">
              <button onClick={exportCsv} className="flex items-center gap-2 nm-card rounded-full px-4 py-2 text-sm text-gray-200 hover:text-[#D4AF37]" data-testid="export-csv"><Download size={15} /> Export CSV</button>
            </div>
            <div className="nm-card rounded-xl overflow-hidden overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead><tr className="text-left text-gray-400 border-b border-white/10 text-xs">
                  <th className="p-4">Name</th><th className="p-4">Service</th><th className="p-4">Contact</th><th className="p-4">Status</th><th className="p-4"></th>
                </tr></thead>
                <tbody>
                  {leads.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-gray-500">No leads yet.</td></tr>}
                  {leads.map((l) => (
                    <tr key={l.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`lead-row-${l.id}`}>
                      <td className="p-4"><p className="text-white font-semibold">{l.full_name}</p><p className="text-gray-500 text-xs">{l.company_name}</p></td>
                      <td className="p-4 text-gray-300">{l.service}</td>
                      <td className="p-4 text-gray-400 text-xs"><p>{l.email}</p><p>{l.phone}</p></td>
                      <td className="p-4">
                        <select value={l.status} onChange={(e) => updateLead(l.id, { status: e.target.value })}
                          className={`text-xs rounded-full px-3 py-1 border capitalize ${STATUS_COLORS[l.status] || ""}`} data-testid={`lead-status-${l.id}`}>
                          {LEAD_STATUSES.map((s) => <option key={s} value={s} className="bg-[#0B132B] text-white">{s}</option>)}
                        </select>
                      </td>
                      <td className="p-4 text-right"><button onClick={() => setSelected(l)} className="text-[#D4AF37] text-xs font-semibold" data-testid={`lead-view-${l.id}`}>View</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "services" && <ServicesTab services={services} reload={load} />}
        {tab === "testimonials" && <TestimonialsTab items={testimonials} reload={load} />}
        {tab === "faqs" && <FaqsTab items={faqs} reload={load} />}
        {tab === "settings" && <SettingsTab settings={settings} reload={load} />}
      </div>

      {/* Lead drawer */}
      {selected && (
        <div className="fixed inset-0 z-[60] flex justify-end" data-testid="lead-drawer">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSelected(null)} />
          <motion.div initial={{ x: 400 }} animate={{ x: 0 }} className="relative w-full max-w-md bg-[#0B132B] border-l border-[#D4AF37]/20 overflow-y-auto p-6">
            <div className="flex justify-between items-start mb-6">
              <div><h3 className="font-head text-xl font-bold text-white">{selected.full_name}</h3><p className="text-gray-400 text-sm">{selected.company_name}</p></div>
              <button onClick={() => setSelected(null)} className="text-gray-400 text-2xl">×</button>
            </div>
            <div className="space-y-2 text-sm">
              {[["Email", selected.email], ["Phone", selected.phone], ["Service", selected.service], ["USDOT", selected.usdot], ["MC", selected.mc_number], ["Trucks", selected.num_trucks], ["Equipment", selected.equipment], ["In Business", selected.years_in_business], ["Source", selected.source]].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-white/5 py-1.5"><span className="text-gray-500">{k}</span><span className="text-white text-right">{v}</span></div>
              ))}
            </div>
            {selected.message && <div className="mt-4"><p className="text-gray-500 text-xs mb-1">Message</p><p className="text-gray-200 text-sm nm-card rounded-lg p-3">{selected.message}</p></div>}
            <div className="mt-5">
              <p className="text-gray-500 text-xs mb-1">Internal Notes</p>
              <textarea className={`${field} min-h-[100px]`} defaultValue={selected.notes}
                onBlur={(e) => updateLead(selected.id, { notes: e.target.value })} data-testid="lead-notes" placeholder="Add a note..." />
            </div>
            <button onClick={() => delLead(selected.id)} className="mt-6 flex items-center gap-2 text-red-400 text-sm" data-testid="lead-delete"><Trash2 size={15} /> Delete lead</button>
          </motion.div>
        </div>
      )}
    </div>
  );
}

function ServicesTab({ services, reload }) {
  const [draft, setDraft] = useState({});
  const save = async (s) => {
    await api.put(`/admin/services/${s.id}`, { price: draft[s.id]?.price ?? s.price, description: draft[s.id]?.description ?? s.description });
    toast.success("Service updated"); reload();
  };
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="services-tab">
      {services.map((s) => (
        <div key={s.id} className="nm-card rounded-xl p-5">
          <p className="font-head font-bold text-white mb-3">{s.num} — {s.title}</p>
          <label className="text-xs text-gray-400">Price</label>
          <input className={field} defaultValue={s.price} onChange={(e) => setDraft((d) => ({ ...d, [s.id]: { ...d[s.id], price: e.target.value } }))} data-testid={`svc-price-${s.id}`} />
          <label className="text-xs text-gray-400 mt-3 block">Description</label>
          <textarea className={`${field} min-h-[70px] mt-1`} defaultValue={s.description} onChange={(e) => setDraft((d) => ({ ...d, [s.id]: { ...d[s.id], description: e.target.value } }))} data-testid={`svc-desc-${s.id}`} />
          <button onClick={() => save(s)} className="mt-3 flex items-center gap-2 nm-gold-bg text-[#0B132B] font-bold text-sm px-4 py-2 rounded-full" data-testid={`svc-save-${s.id}`}><Save size={14} /> Save</button>
        </div>
      ))}
    </div>
  );
}

function TestimonialsTab({ items, reload }) {
  const empty = { name: "", company: "", photo: "", text: "", rating: 5, service: "" };
  const [form, setForm] = useState(empty);
  const add = async () => {
    if (!form.name || !form.text) return toast.error("Name and testimonial are required");
    await api.post("/admin/testimonials", form); setForm(empty); toast.success("Testimonial added"); reload();
  };
  const del = async (id) => { await api.delete(`/admin/testimonials/${id}`); toast.success("Removed"); reload(); };
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="testimonials-tab">
      <div className="nm-card rounded-xl p-5 space-y-3 h-fit">
        <p className="font-head font-bold text-white flex items-center gap-2"><Plus size={16} /> Add testimonial</p>
        <input className={field} placeholder="Customer name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="t-name" />
        <input className={field} placeholder="Company name" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="t-company" />
        <input className={field} placeholder="Photo URL (optional)" value={form.photo} onChange={(e) => setForm({ ...form, photo: e.target.value })} data-testid="t-photo" />
        <input className={field} placeholder="Service used" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} data-testid="t-service" />
        <select className={field} value={form.rating} onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })} data-testid="t-rating">
          {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} stars</option>)}
        </select>
        <textarea className={`${field} min-h-[90px]`} placeholder="Testimonial text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="t-text" />
        <button onClick={add} className="nm-gold-bg text-[#0B132B] font-bold text-sm px-4 py-2 rounded-full w-full" data-testid="t-add">Add Testimonial</button>
      </div>
      <div className="space-y-3">
        {items.length === 0 && <p className="text-gray-500 text-sm">No testimonials yet. Add real client stories here.</p>}
        {items.map((t) => (
          <div key={t.id} className="nm-card rounded-xl p-5" data-testid={`t-item-${t.id}`}>
            <div className="flex justify-between">
              <div className="flex gap-1 mb-2">{[...Array(t.rating)].map((_, i) => <Star key={i} size={13} className="fill-[#D4AF37] text-[#D4AF37]" />)}</div>
              <button onClick={() => del(t.id)} className="text-red-400"><Trash2 size={15} /></button>
            </div>
            <p className="text-gray-200 text-sm italic">"{t.text}"</p>
            <p className="text-white font-semibold text-sm mt-2">{t.name} <span className="text-gray-500 font-normal">{t.company}</span></p>
          </div>
        ))}
      </div>
    </div>
  );
}

function FaqsTab({ items, reload }) {
  const [form, setForm] = useState({ question: "", answer: "" });
  const add = async () => {
    if (!form.question || !form.answer) return toast.error("Question and answer required");
    await api.post("/admin/faqs", form); setForm({ question: "", answer: "" }); toast.success("FAQ added"); reload();
  };
  const del = async (id) => { await api.delete(`/admin/faqs/${id}`); toast.success("Removed"); reload(); };
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="faqs-tab">
      <div className="nm-card rounded-xl p-5 space-y-3 h-fit">
        <p className="font-head font-bold text-white flex items-center gap-2"><Plus size={16} /> Add FAQ</p>
        <input className={field} placeholder="Question" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="faq-q" />
        <textarea className={`${field} min-h-[90px]`} placeholder="Answer" value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} data-testid="faq-a" />
        <button onClick={add} className="nm-gold-bg text-[#0B132B] font-bold text-sm px-4 py-2 rounded-full w-full" data-testid="faq-add">Add FAQ</button>
      </div>
      <div className="space-y-3">
        {items.map((f) => (
          <div key={f.id} className="nm-card rounded-xl p-5" data-testid={`faq-item-${f.id}`}>
            <div className="flex justify-between gap-3">
              <p className="text-white font-semibold text-sm">{f.question}</p>
              <button onClick={() => del(f.id)} className="text-red-400 shrink-0"><Trash2 size={15} /></button>
            </div>
            <p className="text-gray-400 text-sm mt-2">{f.answer}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsTab({ settings, reload }) {
  const [form, setForm] = useState(settings);
  useEffect(() => { setForm(settings); }, [settings]);
  const save = async () => { await api.put("/admin/settings", form); toast.success("Settings saved"); reload(); };
  const fields = [["phone", "Phone number"], ["email", "Contact email"], ["address", "Address"], ["facebook", "Facebook URL"], ["instagram", "Instagram URL"], ["linkedin", "LinkedIn URL"], ["tiktok", "TikTok URL"]];
  return (
    <div className="nm-card rounded-xl p-6 max-w-xl space-y-3" data-testid="settings-tab">
      <p className="font-head font-bold text-white flex items-center gap-2 mb-2"><MessageSquare size={16} /> Contact & social links</p>
      {fields.map(([k, label]) => (
        <div key={k}>
          <label className="text-xs text-gray-400">{label}</label>
          <input className={field} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`set-${k}`} />
        </div>
      ))}
      <button onClick={save} className="flex items-center gap-2 nm-gold-bg text-[#0B132B] font-bold text-sm px-5 py-2.5 rounded-full mt-2" data-testid="settings-save"><Save size={15} /> Save Settings</button>
    </div>
  );
}
